import { useEffect, useRef, useState } from 'react';
import { Select, SelectItem } from '@nextui-org/react';
import { createPortal } from 'react-dom';
import useFilterStore from '../../stores/useFiltersStore';
import useUserStore from '../../stores/useUserStore';
import {
  getExporterDivisions,
  type ExporterDivision,
} from '../../api/exporter-divisions';
import { useTranslation } from '../../hooks/useTranslation';

const ALL = 'all';

interface DivisionFilterProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  collapsed?: boolean;
}

// Global division filter for exporters with more than one division. It
// renders nothing otherwise, so single-division exporters never see it.
const DivisionFilter = ({
  className = 'min-w-32',
  size = 'sm',
  collapsed = false,
}: DivisionFilterProps) => {
  const { t } = useTranslation('common');
  const { userProfile } = useUserStore();
  const { selectedDivisionId, setSelectedDivisionId } = useFilterStore();
  const exporterId = userProfile?.exporter?.id ?? userProfile?.exporter_id;
  const [divisions, setDivisions] = useState<ExporterDivision[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!exporterId) {
      setDivisions([]);
      return;
    }
    let active = true;
    getExporterDivisions(exporterId)
      .then((rows) => {
        if (active) setDivisions(rows);
      })
      .catch((error) => console.error('Error loading divisions:', error));
    return () => {
      active = false;
    };
  }, [exporterId]);

  // A persisted id from another exporter (or a deleted division) means "all".
  useEffect(() => {
    if (
      selectedDivisionId !== null &&
      divisions.length > 0 &&
      !divisions.some((d) => d.id === selectedDivisionId)
    )
      setSelectedDivisionId(null);
  }, [divisions, selectedDivisionId, setSelectedDivisionId]);

  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({ top: rect.top, left: rect.right + 10 });
    }
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('.division-dropdown')
      )
        setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (divisions.length <= 1) return null;

  const allLabel = t('Todas las divisiones');
  const selected = divisions.find((d) => d.id === selectedDivisionId) ?? null;
  const options = [{ id: ALL, name: allLabel }].concat(
    divisions.map((d) => ({ id: String(d.id), name: d.name }))
  );
  const choose = (key: string) => {
    setSelectedDivisionId(key === ALL ? null : Number(key));
    setShowDropdown(false);
  };

  if (collapsed) {
    const initials = selected
      ? selected.name.trim().slice(0, 2).toUpperCase()
      : t('Todas').slice(0, 2).toUpperCase();
    return (
      <div className='relative'>
        <div
          ref={buttonRef}
          className='w-10 h-10 rounded-full flex items-center justify-center bg-background border border-divider shadow-sm cursor-pointer hover:bg-default-100'
          onClick={() => setShowDropdown(!showDropdown)}
          title={selected?.name ?? allLabel}
        >
          <span
            className='text-xs font-bold text-default-600'
            aria-label={`${t('División')} ${selected?.name ?? allLabel}`}
          >
            {initials}
          </span>
        </div>
        {showDropdown
          ? createPortal(
              <div
                className='division-dropdown fixed py-1 w-auto min-w-[150px] bg-background rounded-md shadow-lg z-[999999] border border-divider'
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                }}
              >
                {options.map((option) => (
                  <div
                    key={option.id}
                    className={`px-4 py-2 text-sm cursor-pointer hover:bg-default-200 flex items-center ${
                      (selected ? String(selected.id) : ALL) === option.id
                        ? 'bg-default-100 font-medium'
                        : ''
                    }`}
                    onClick={() => choose(option.id)}
                  >
                    <span>{option.name}</span>
                  </div>
                ))}
              </div>,
              document.body
            )
          : null}
      </div>
    );
  }

  return (
    <div className='relative w-full'>
      <Select
        label={t('División')}
        selectedKeys={[selected ? String(selected.id) : ALL]}
        onChange={(e) => choose(e.target.value || ALL)}
        className={className}
        size={size}
        aria-label={t('Selecciona división')}
        disallowEmptySelection
      >
        {options.map((option) => (
          <SelectItem key={option.id} value={option.id}>
            {option.name}
          </SelectItem>
        ))}
      </Select>
    </div>
  );
};

export default DivisionFilter;
